import { useTranslation } from 'react-i18next'
import {
  BellRing,
  ChartColumnIncreasing,
  Compass,
  Newspaper,
  Radar,
  SmilePlus,
  Tags,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useScrollReveal } from './useScrollReveal'

const features: { key: string; icon: LucideIcon }[] = [
  { key: 'socialListening', icon: Radar },
  { key: 'sentimentAnalysis', icon: SmilePlus },
  { key: 'keywordTracking', icon: Tags },
  { key: 'newsMonitoring', icon: Newspaper },
  { key: 'alerts', icon: BellRing },
  { key: 'competitorMonitoring', icon: Compass },
  { key: 'analyticsDashboard', icon: ChartColumnIncreasing },
]

function FeatureItem({
  featureKey,
  icon: Icon,
}: {
  featureKey: string
  icon: LucideIcon
}) {
  const { t } = useTranslation()
  const { ref, isVisible } = useScrollReveal({ threshold: 0.1 })

  return (
    <article
      ref={ref}
      className={cn(
        'scroll-reveal group flex gap-5 rounded-xl p-4 transition-colors duration-200 hover:bg-glass-hover sm:p-5',
        isVisible && 'is-visible',
      )}
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-icon-frame text-icon-frame-foreground ring-1 ring-icon-frame-ring transition-colors duration-200">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <h3 className="m-0 text-base font-medium text-foreground">
          {t(`landing.features.items.${featureKey}.title`)}
        </h3>
        <p className="m-0 mt-1.5 text-sm leading-relaxed text-muted-foreground">
          {t(`landing.features.items.${featureKey}.description`)}
        </p>
      </div>
    </article>
  )
}

export function FeaturesSection() {
  const { t } = useTranslation()

  return (
    <section
      id="features"
      className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
    >
      <div className="mb-10 max-w-2xl">
        <h2 className="m-0 text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
          {t('landing.features.title')}
        </h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          {t('landing.features.subtitle')}
        </p>
      </div>

      <div className="mx-auto max-w-3xl divide-y divide-border">
        {features.map((item) => (
          <FeatureItem key={item.key} featureKey={item.key} icon={item.icon} />
        ))}
      </div>
    </section>
  )
}
